import type { Locale } from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button } from "@chams-pay/design-system/components/button";
import { Download, Printer } from "lucide-react";
import type { ReportMode, ReportsController } from "../../core/use-operations.js";
import { A4DocumentPreview } from "../../ui/a4-document-preview.js";
import { DatePicker } from "../../ui/date-picker.js";
import { MonthPicker } from "../../ui/month-picker.js";
import { ReportDocument } from "../../ui/report-document.js";
import { CalendarGlyph, ScopedEmpty, ScopedError, SkeletonLines } from "../../ui/states.js";

const modeKey = {
  daily: "ops.daily",
  monthly: "ops.monthly",
} as const satisfies Record<ReportMode, Parameters<typeof translate>[1]>;

export function DesktopReportsScreen({
  locale,
  controller,
}: {
  readonly locale: Locale;
  readonly controller: ReportsController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const empty = controller.report !== null && controller.report.activity.length === 0;

  return (
    <section className="operations-screen reports-screen" data-testid="reports-screen">
      <div className="reports-workspace">
        <header className="report-controls no-print">
          {controller.mode === "daily" ? (
            <DatePicker
              locale={locale}
              className="report-date-control"
              label={t("ops.date")}
              value={controller.date}
              onChange={controller.setDate}
            />
          ) : (
            <MonthPicker
              locale={locale}
              className="report-date-control"
              label={t("ops.monthly")}
              value={controller.month}
              onChange={controller.setMonth}
            />
          )}
          <div
            role="tablist"
            aria-label={t("ops.reports")}
            data-tab-count="2"
            data-active-index={controller.mode === "daily" ? "0" : "1"}
            className="sliding-tabs ms-auto flex gap-[3px] overflow-hidden rounded-lg bg-sunken p-[3px]"
          >
            <span className="sliding-tabs__indicator" aria-hidden="true" />
            {(["daily", "monthly"] as const).map((mode) => {
              const active = controller.mode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => controller.setMode(mode)}
                  className="h-11 min-h-11 rounded-md px-4 font-heading text-xs font-bold"
                >
                  {t(modeKey[mode])}
                </button>
              );
            })}
          </div>
        </header>

        <div className="report-actionbar no-print">
          <Button
            variant="outline"
            className="min-h-11"
            disabled={!controller.report || controller.busy}
            onClick={controller.print}
          >
            <Printer aria-hidden="true" />
            {t("ops.print")}
          </Button>
          <Button
            variant="outline"
            className="min-h-11"
            disabled={!controller.report || controller.busy}
            onClick={() => void controller.download()}
          >
            <Download aria-hidden="true" />
            {t("ops.download")}
          </Button>
        </div>

        {controller.busy ? (
          <div className="report-preview-canvas">
            <div className="report-paper-loading" role="status" aria-live="polite">
              <SkeletonLines className="report-loading-lines" />
              <span className="sr-only">{t("ops.generating")}</span>
            </div>
          </div>
        ) : controller.failed ? (
          <div className="report-state">
            <ScopedError
              locale={locale}
              title={t("ops.reportFailed")}
              body={t(controller.failureBodyKey)}
              onRetry={() => void controller.reload()}
            />
          </div>
        ) : empty ? (
          <div className="report-state">
            <ScopedEmpty
              glyph={<CalendarGlyph />}
              title={t("ops.noMovements")}
              body={t("ops.noMovementsBody")}
            />
          </div>
        ) : controller.report ? (
          <div
            className="report-paper-desk report-paper-desk--a4-preview report-preview-canvas"
            data-document-preview-canvas
          >
            <A4DocumentPreview kind="report" locale={locale}>
              <ReportDocument fitA4Preview locale={locale} report={controller.report} />
            </A4DocumentPreview>
          </div>
        ) : null}
      </div>
    </section>
  );
}
